import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router'
import { isSignedIn } from '../api/auth'
import { RouteMessage } from '../components/RouteMessage'
import { isEmptyQueueSearch, validateQueueSearch } from '../features/queue/searchParams'
import { useQueueViewStore } from '../features/queue/queueViewStore'
import { parseMode, type WorkspaceMode } from '../features/workspace/workspaceMode'
import { SessionBridge } from './sessionBridge'

/**
 * The route tree — docs/spec/01 §4.
 *
 * Routes are lazy from the first commit (01 §5). Adding code-splitting later
 * means discovering later that something got imported eagerly and blew the
 * 05 §5 budget; starting split makes a regression visible the day it lands.
 */

const rootRoute = createRootRoute({
  component: () => (
    <>
      <SessionBridge />
      <Outlet />
    </>
  ),
  notFoundComponent: () => (
    <RouteMessage
      title="Page not found"
      description="That page doesn't exist. It may have been moved or the link may be wrong."
    />
  ),
})

export interface LoginSearch {
  /** Where to return after signing in — set by the guard below. */
  readonly redirect?: string | undefined
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const value = search['redirect']
    // Only same-origin paths. Accepting an arbitrary URL here would turn the
    // login screen into an open redirect — a phishing primitive on a page that
    // already has the user's full attention and their password.
    const safe = typeof value === 'string' && value.startsWith('/') && !value.startsWith('//')
    // `redirect: undefined` and NOT `{}` — see searchParams.ts. An omitted key
    // does not reject anything: the parent match's raw search is merged under
    // this result, so a rejected value would survive. The real defence is
    // `safeRedirect` at the point of use; this keeps the two consistent.
    return { redirect: safe ? value : undefined }
  },
  component: lazyRouteComponent(() => import('../features/auth/LoginScreen')),
})

/**
 * Everything behind auth hangs off this pathless layout: one guard, one shell,
 * no chance of adding a route that forgets either.
 */
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: ({ location }) => {
    if (!isSignedIn()) {
      // Preserve the intended URL so the user lands where they were going,
      // not on a generic home page (01 §4).
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
  },
  component: lazyRouteComponent(() => import('../features/shell/AppShell')),
})

const indexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/queue' })
  },
})

const queueRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/queue',
  // The queue's filters live in the URL (01 §3, 05 §2) so a view can be shared
  // and restored. `validateQueueSearch` is total and falls back on anything it
  // does not recognise — a hand-edited URL must not be able to reach the api's
  // 500-on-`?page=abc` (02 §3).
  validateSearch: validateQueueSearch,
  /**
   * Put the operator back where they were (04 §3c).
   *
   * **Here, not in the component.** After mount the bare queue would already
   * have fired a list request for the default filters, and the restored one
   * fires a second — two requests for one navigation, against the 05 §5 budget.
   * `beforeLoad` runs before the component, so the discarded state never
   * reaches the api.
   *
   * **On route ENTRY only**, which is what makes "Clear all" survive: clearing
   * happens while already on the queue, so this never runs and the just-cleared
   * filters cannot snap back.
   *
   * `replace` so Back does not land on the bare URL and bounce here again.
   */
  beforeLoad: ({ search, cause }) => {
    /**
     * `cause !== 'enter'` is not a belt-and-braces extra check — it is the
     * ONLY thing that makes "on route entry only" true.
     *
     * `beforeLoad` reruns on every navigation that resolves to this route,
     * including an in-place search change while already on `/queue`
     * (`cause: 'stay'`) — verified directly: clicking "Clear all" produces a
     * bare search, which re-enters this guard mid-navigation, before the
     * screen has re-rendered with the new (empty) search and before the view
     * store has recorded it. Without this check the guard reads the STALE
     * remembered view (from before the click) and redirects the "cleared"
     * navigation straight back to the filters the operator just cleared —
     * the exact bug this route exists to prevent, just self-inflicted.
     * `cause: 'enter'` is TanStack Router's own signal for "this match did
     * not exist a moment ago" — a fresh mount or a transition from a
     * different route — which is the only time restoring is correct.
     */
    if (cause !== 'enter') return
    if (!isEmptyQueueSearch(search)) return
    const remembered = useQueueViewStore.getState().search
    // A remembered-but-empty view would redirect to an identical URL, re-enter
    // this guard, and loop.
    if (remembered === null || isEmptyQueueSearch(remembered)) return
    throw redirect({ to: '/queue', search: remembered, replace: true })
  },
  component: lazyRouteComponent(() => import('../features/queue/QueueScreen')),
})

const workspaceRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/jobs/$jobId',
  // `?mode=` is a HINT about which screen to open, narrowed here so nothing
  // downstream ever sees an arbitrary string. It cannot grant anything —
  // `resolveAccess` still decides what the user may do (04 §4c). Returning the
  // key explicitly, even as `undefined`, is what stops an unrecognised value
  // passing straight through (the trap that let `?page=abc` reach the api).
  // The key is OPTIONAL in the type (so linking to a job needs no `search`)
  // but always RETURNED at runtime — that is what replaces an unrecognised
  // value rather than letting it through.
  validateSearch: (raw: Record<string, unknown>): { mode?: WorkspaceMode | undefined } => ({
    mode: parseMode(raw['mode']),
  }),
  component: lazyRouteComponent(() => import('../features/workspace/WorkspaceScreen')),
})

/**
 * Admin-only. The role check lives in the component, not this guard, because
 * 04 §7 wants a "you don't have access" STATE rather than a redirect — being
 * bounced somewhere else with no explanation is how an operator concludes the
 * app is broken.
 */
const labelsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/labels',
  component: lazyRouteComponent(() => import('../features/labels/LabelsScreen')),
})

export const routeTree = rootRoute.addChildren([
  loginRoute,
  authenticatedRoute.addChildren([indexRoute, queueRoute, workspaceRoute, labelsRoute]),
])

/**
 * The one place router options are set. Tests build their router through
 * this factory too (`src/test/app.tsx`), rather than calling `createRouter`
 * directly with a hand-picked subset of options — a test router that quietly
 * drops `defaultPreload: 'intent'` never exercises `beforeLoad`'s
 * `cause: 'preload'` path at all, which is exactly the gap that let the
 * queue's preload-exclusion guard (04 §3c, discovery 2) ship with zero
 * coverage. `history` is the only thing a caller should ever need to
 * override — production takes the browser history, tests a memory one.
 */
export function createAppRouter(overrides?: { readonly history?: RouterHistory }) {
  return createRouter({ routeTree, defaultPreload: 'intent', ...overrides })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
